import { useEffect, useRef, useState } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { getHubUrl } from "../utils/apiConfig";
import { navigate } from "../services/navigation";

// Same visibility rule as the notification panel: no role means full visibility.
export const canReceiveOrderAlerts = (role) => {
  if (!role) return true;
  const roleName = typeof role === "string" ? role : (role.role || role.name || "");
  if (["admin", "administrator"].includes(roleName.toLowerCase())) return true;
  return typeof role === "object" && Boolean(role.orders || role.ordersIncoming);
};

const isSupported = () => typeof window !== "undefined" && "Notification" in window;

const currentPermission = () => (isSupported() ? window.Notification.permission : "unsupported");

// Browser notification for accepted orders. Always on; the browser's notification permission is the only switch.
// Works only while an app tab is open.
export default function useOrderArrivalAlerts(user, onOrderArrived) {
  const key = user?.userId ?? user?.id ?? user?.username ?? user?.userName ?? null;
  const signedIn = Boolean(user);
  const allowed = signedIn && canReceiveOrderAlerts(user?.role);
  const [permission, setPermission] = useState(currentPermission);
  const enabled = allowed && permission === "granted";

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const arrivedRef = useRef(onOrderArrived);
  arrivedRef.current = onOrderArrived;
  const seenRef = useRef(new Set());
  const askedRef = useRef(false);

  // Browsers only show the prompt (or a non-muted one) in response to a user gesture,
  // so ask once per session on the first click or key press while the choice is undecided.
  useEffect(() => {
    if (!allowed || permission !== "default" || askedRef.current) return undefined;
    const remove = () => {
      window.removeEventListener("pointerdown", ask, true);
      window.removeEventListener("keydown", ask, true);
    };
    async function ask() {
      if (askedRef.current) return;
      askedRef.current = true;
      remove();
      try {
        // Older Safari uses a callback and returns undefined.
        const result = await Promise.resolve(window.Notification.requestPermission());
        setPermission(result || currentPermission());
      } catch {
        setPermission(currentPermission());
      }
    }
    window.addEventListener("pointerdown", ask, true);
    window.addEventListener("keydown", ask, true);
    return remove;
  }, [allowed, permission]);

  // Pick up changes made in the browser's site settings without a reload.
  useEffect(() => {
    if (!isSupported()) return undefined;
    const sync = () => setPermission(currentPermission());
    let status = null;
    let active = true;
    const query = navigator.permissions?.query;
    if (typeof query === "function") {
      query.call(navigator.permissions, { name: "notifications" })
        .then((result) => { if (active) { status = result; status.onchange = sync; } })
        .catch(() => {});
    }
    window.addEventListener("focus", sync);
    return () => {
      active = false;
      if (status) status.onchange = null;
      window.removeEventListener("focus", sync);
    };
  }, []);

  useEffect(() => {
    if (!signedIn) return undefined;
    const connection = new HubConnectionBuilder()
      .withUrl(getHubUrl("workflow"), { accessTokenFactory: () => sessionStorage.getItem("token") || "" })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
    let active = true;

    const handleLifecycleEvent = (event) => {
      const data = event?.data;
      // Cancellations also send ACTIVE_UPDATE; only accepted orders ("K") are arrivals.
      if (!active || event?.type !== "ACTIVE_UPDATE" || data?.statusCode !== "K") return;
      arrivedRef.current?.(data);
      if (!enabledRef.current || data.id == null || seenRef.current.has(data.id)) return;
      seenRef.current.add(data.id);
      try {
        // The per-order tag lets the browser collapse duplicates from several open tabs.
        const alert = new window.Notification(`New order ${data.orderNumber || ""}`.trim(), {
          body: data.accountNumber ? `Account ${data.accountNumber}` : "A new order has arrived.",
          tag: `order-${data.id}`,
        });
        alert.onclick = () => {
          window.focus();
          navigate("/OrderDashboard");
          alert.close();
        };
      } catch (err) {
        // Some mobile browsers only allow notifications from a service worker.
        console.warn("Browser order notification failed", err);
      }
    };

    connection.on("OrderLifecycleEvent", handleLifecycleEvent);
    connection.start().catch((err) => {
      if (active) console.warn("Order alert connection failed:", err);
    });

    return () => {
      active = false;
      connection.off("OrderLifecycleEvent", handleLifecycleEvent);
      connection.stop().catch(() => {});
    };
  }, [signedIn, key]);

  return { supported: isSupported(), allowed, permission, enabled };
}
